import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Email as EmailIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../config/axios';

type EmailStatus = '' | 'completed' | 'skipped' | 'failed';

interface EmailStats {
  total: string | number;
  completed: string | number;
  skipped: string | number;
  failed: string | number;
  last_received: string | null;
}

interface InvoiceDetail {
  id: number;
  invoice_number: string;
  total_amount: string | number | null;
  status: string;
}

interface ProcessedEmail {
  id: number;
  message_subject: string;
  sender_email: string;
  sender_name: string | null;
  received_date: string | null;
  processing_status: 'completed' | 'skipped' | 'failed' | string;
  attachment_count: number;
  error_message: string | null;
  mailbox_name: string | null;
  invoice_details: InvoiceDetail[];
  created_at: string;
}

const statusLabels: Record<string, string> = {
  completed: 'Zpracováno',
  skipped: 'Přeskočeno',
  failed: 'Chyba',
};

const statusColors: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  completed: 'success',
  skipped: 'warning',
  failed: 'error',
};

const formatDate = (value: string | null) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('cs-CZ');
};

const MyEmails: React.FC = () => {
  const navigate = useNavigate();
  const [emails, setEmails] = useState<ProcessedEmail[]>([]);
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [status, setStatus] = useState<EmailStatus>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadEmails = async () => {
    setLoading(true);
    setError(null);

    try {
      const query = status ? `?status=${status}` : '';
      const [emailsResponse, statsResponse] = await Promise.all([
        api.get(`/user/my-emails${query}`),
        api.get('/user/my-emails-stats'),
      ]);

      setEmails(emailsResponse.data.emails || []);
      setStats(statsResponse.data.stats || null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Nepodařilo se načíst přehled e-mailů.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <EmailIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
              Přehled e-mailů
            </Typography>
            <Typography color="text.secondary">
              E-maily, které systém načetl a zpracoval pro váš účet.
            </Typography>
          </Box>
        </Box>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadEmails}
          disabled={loading}
        >
          Obnovit
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>Celkem</Typography>
            <Typography variant="h4">{stats?.total ?? 0}</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>Zpracováno</Typography>
            <Typography variant="h4" color="success.main">{stats?.completed ?? 0}</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>Přeskočeno</Typography>
            <Typography variant="h4" color="warning.main">{stats?.skipped ?? 0}</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>Chyby</Typography>
            <Typography variant="h4" color="error.main">{stats?.failed ?? 0}</Typography>
          </CardContent>
        </Card>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Typography color="text.secondary">
            Poslední přijatý e-mail: {formatDate(stats?.last_received || null)}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Stav</InputLabel>
            <Select
              label="Stav"
              value={status}
              onChange={(event) => setStatus(event.target.value as EmailStatus)}
            >
              <MenuItem value="">Vše</MenuItem>
              <MenuItem value="completed">Zpracováno</MenuItem>
              <MenuItem value="skipped">Přeskočeno</MenuItem>
              <MenuItem value="failed">Chyba</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Předmět</TableCell>
                <TableCell>Schránka</TableCell>
                <TableCell>Přijato</TableCell>
                <TableCell>Stav</TableCell>
                <TableCell>Faktury</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {emails.map((email) => (
                <TableRow key={email.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {email.message_subject || '(Bez předmětu)'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {email.sender_name ? `${email.sender_name} ` : ''}
                      &lt;{email.sender_email}&gt;
                    </Typography>
                    {email.error_message && (
                      <Typography variant="caption" color="error" display="block">
                        {email.error_message}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{email.mailbox_name || '-'}</TableCell>
                  <TableCell>{formatDate(email.received_date || email.created_at)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={statusLabels[email.processing_status] || email.processing_status}
                      color={statusColors[email.processing_status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {email.invoice_details?.length ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {email.invoice_details.map((invoice) => (
                          <Button
                            key={invoice.id}
                            size="small"
                            variant="text"
                            startIcon={<ReceiptIcon />}
                            onClick={() => navigate(`/invoices/${invoice.id}`)}
                            sx={{ justifyContent: 'flex-start' }}
                          >
                            {invoice.invoice_number || `Faktura ${invoice.id}`}
                          </Button>
                        ))}
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {emails.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography sx={{ py: 3 }} color="text.secondary">
                      Žádné e-maily k zobrazení.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default MyEmails;
